import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import Layout, { PageTitle } from "@/components/Layout";
import { NAV_ADMIN } from "@/lib/nav";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Eye, EyeOff } from "lucide-react";
import { FilterBar } from "@/components/FilterBar";

const F_EMPTY = { nama: "", peran: "" };
const SYSTEM_ROLES = ["admin", "kepala_sekolah", "bendahara", "pustaka"];
const ROLE_LABEL = {
  admin: "Administrator", kepala_sekolah: "Kepala Sekolah", bendahara: "Bendahara",
  pustaka: "Petugas Pustaka", guru: "Guru", orangtua: "Orang Tua",
};
const PERAN_OPTS = [
  { v: "admin", label: "Administrator" }, { v: "kepala_sekolah", label: "Kepala Sekolah" },
  { v: "bendahara", label: "Bendahara" }, { v: "pustaka", label: "Petugas Pustaka" },
  { v: "guru", label: "Guru" }, { v: "orangtua", label: "Orang Tua" },
];

export default function AdminUsers() {
  const [all, setAll] = useState([]);
  const [items, setItems] = useState([]);
  const [siswa, setSiswa] = useState([]);
  const [guru, setGuru] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ username: "", password: "", peran: "guru", linked_id: "", nama: "" });
  const [showPwd, setShowPwd] = useState({});
  const [filters, setFilters] = useState(F_EMPTY);
  const [applied, setApplied] = useState(F_EMPTY);
  const [editUser, setEditUser] = useState(null); // {id, nama, username, password}
  const [savingEdit, setSavingEdit] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ data: u }, { data: s }, { data: g }] = await Promise.all([
        api.get("/users"),
        api.get("/siswa", { params: { status: "aktif" } }),
        api.get("/guru", { params: { status: "aktif" } }),
      ]);
      // Akun sistem (admin/kepsek/bendahara/pustaka) dipin di atas, lalu guru & ortu
      const order = { admin: 0, kepala_sekolah: 1, bendahara: 2, pustaka: 3, guru: 4, orangtua: 5 };
      const sorted = [...u].sort((a, b) => (order[a.role] ?? 9) - (order[b.role] ?? 9));
      setAll(sorted);
      setSiswa(s); setGuru(g);
    } catch (e) { console.error("Load users failed:", e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let rows = all;
    if (applied.nama) rows = rows.filter(u => (u.nama || "").toLowerCase().includes(applied.nama.toLowerCase()));
    if (applied.peran) rows = rows.filter(u => u.role === applied.peran);
    setItems(rows);
  }, [all, applied]);

  const onApply = () => setApplied({ ...filters });
  const onReset = () => { setFilters(F_EMPTY); setApplied(F_EMPTY); };

  const sourceList = form.peran === "guru" ? guru : siswa;
  const setLinked = (id) => {
    const item = sourceList.find(x => x.id === id);
    setForm({ ...form, linked_id: id, nama: item?.nama || "" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.linked_id) return toast.error("Pilih nama dahulu");
    try {
      await api.post("/users", form);
      toast.success("Akun dibuat");
      setShowForm(false); setForm({ username: "", password: "", peran: "guru", linked_id: "", nama: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Hapus akun ini?")) return; await api.delete(`/users/${id}`); toast.success("Akun dihapus"); load(); };

  const openEdit = (u) => setEditUser({ id: u.id, nama: u.nama, username: u.username, password: "" });
  const saveEdit = async () => {
    if (!editUser) return;
    const payload = { username: (editUser.username || "").trim() };
    if (!payload.username) return toast.error("Username tidak boleh kosong");
    if (editUser.password) {
      if (editUser.password.length < 6) return toast.error("Password minimal 6 karakter");
      payload.password = editUser.password;
    }
    setSavingEdit(true);
    try {
      await api.put(`/users/${editUser.id}`, payload);
      toast.success("Perubahan disimpan");
      setEditUser(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingEdit(false); }
  };

  return (
    <Layout nav={NAV_ADMIN}>
      <PageTitle title="Manajemen Pengguna" action={
        <button onClick={() => setShowForm(true)} className="pp-btn-primary" data-testid="open-create-user"><Plus className="w-4 h-4" />Tambah Pengguna</button>
      } />

      <FilterBar
        filters={filters} setFilters={setFilters} onApply={onApply} onReset={onReset}
        dataTestidPrefix="user-filter"
        fields={[
          { key: "nama", type: "text", label: "Nama", placeholder: "Cari nama" },
          { key: "peran", type: "select", label: "Peran", options: PERAN_OPTS },
        ]}
      />

      {showForm && (
        <section className="pp-card p-6 mb-6 animate-fade-up">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-lg font-semibold">Tambah Pengguna</h3>
            <button onClick={() => setShowForm(false)}><X className="w-5 h-5 text-slate-400" /></button>
          </div>
          <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="user-form">
            <div><label className="pp-label">Peran *</label>
              <select className="pp-input" value={form.peran} onChange={(e) => setForm({...form, peran: e.target.value, linked_id: "", nama: ""})} data-testid="user-peran">
                <option value="guru">Guru</option><option value="orangtua">Orang Tua (Siswa)</option>
              </select></div>
            <div><label className="pp-label">Nama ({form.peran === "guru" ? "Guru" : "Siswa"}) *</label>
              <select className="pp-input" required value={form.linked_id} onChange={(e) => setLinked(e.target.value)} data-testid="user-linked">
                <option value="">Pilih {form.peran === "guru" ? "guru" : "siswa"}</option>
                {sourceList.map((x) => <option key={x.id} value={x.id}>{x.nama}</option>)}
              </select></div>
            <div><label className="pp-label">Username *</label><input required className="pp-input" value={form.username} onChange={(e) => setForm({...form, username: e.target.value})} data-testid="user-username" /></div>
            <div><label className="pp-label">Password *</label><input required minLength={6} className="pp-input" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} data-testid="user-password" /></div>
            <div className="sm:col-span-2 flex justify-end gap-2">
              <button type="button" onClick={() => setShowForm(false)} className="pp-btn-secondary">Batal</button>
              <button type="submit" className="pp-btn-primary" data-testid="submit-user">Simpan Pengguna</button>
            </div>
          </form>
        </section>
      )}

      <section className="pp-card pp-freeze overflow-hidden">
        <table className="w-full text-sm" data-testid="users-table">
          <thead><tr className="pp-table-header">
            <th className="text-left px-5 py-3">Nama</th>
            <th className="text-left px-5 py-3">Username</th>
            <th className="text-left px-5 py-3">Password</th>
            <th className="text-left px-5 py-3">Peran</th>
            <th className="text-left px-5 py-3">Tgl Dibuat</th>
            <th className="text-right px-5 py-3">Aksi</th>
          </tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-slate-400 italic">Belum ada pengguna.</td></tr>}
            {items.map((u) => {
              const isSystem = SYSTEM_ROLES.includes(u.role);
              return (
              <tr key={u.id} className="pp-row" data-testid={`user-row-${u.id}`}>
                <td className="px-5 py-3 font-medium">{u.nama}</td>
                <td className="px-5 py-3 font-mono text-xs">{u.username}</td>
                <td className="px-5 py-3 font-mono text-xs">
                  <button onClick={() => setShowPwd((s) => ({...s, [u.id]: !s[u.id]}))} className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-900">
                    {showPwd[u.id] ? (u.password_plain || "—") : "••••••"} {showPwd[u.id] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  </button>
                </td>
                <td className="px-5 py-3"><span className={isSystem ? "pp-badge-warning" : "pp-badge-info"}>{ROLE_LABEL[u.role] || u.role}</span></td>
                <td className="px-5 py-3 text-xs text-slate-500">{u.created_at?.slice(0, 10)}</td>
                <td className="px-5 py-3 text-right">
                  <div className="inline-flex gap-1">
                    <button onClick={() => openEdit(u)} className="pp-btn-ghost text-xs" data-testid={`user-edit-${u.id}`}><Pencil className="w-3.5 h-3.5" />Edit</button>
                    {!isSystem && (
                      <button onClick={() => del(u.id)} className="pp-btn-danger" data-testid={`user-delete-${u.id}`}><Trash2 className="w-3.5 h-3.5" />Hapus</button>
                    )}
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      {editUser && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="user-edit-modal">
          <div className="bg-white rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-semibold">Edit Akun · <span className="text-slate-500 text-sm font-normal">{editUser.nama}</span></h3>
              <button onClick={() => setEditUser(null)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="pp-label">Username *</label>
                <input
                  className="pp-input"
                  value={editUser.username}
                  onChange={(e) => setEditUser({ ...editUser, username: e.target.value })}
                  data-testid="user-edit-username"
                />
              </div>
              <div>
                <label className="pp-label">Password Baru <span className="text-slate-400 font-normal">(kosongkan bila tidak diubah)</span></label>
                <input
                  type="text"
                  className="pp-input"
                  placeholder="Minimal 6 karakter"
                  value={editUser.password}
                  onChange={(e) => setEditUser({ ...editUser, password: e.target.value })}
                  data-testid="user-edit-password"
                />
              </div>
              <p className="text-xs text-slate-500">Hanya username &amp; password yang dapat diubah di sini. Nama &amp; peran mengikuti data master.</p>
            </div>
            <div className="flex justify-end gap-2 mt-5 pt-4 border-t border-slate-200">
              <button onClick={() => setEditUser(null)} className="pp-btn-secondary">Batal</button>
              <button onClick={saveEdit} disabled={savingEdit} className="pp-btn-primary" data-testid="user-edit-save">
                {savingEdit ? "Menyimpan…" : "Simpan"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
